import { CircleLazyImage } from '@bilibili-maoer/prelude'
import { api } from '@bilibili-maoer/web-utils'

import UnfoldLessDoubleIcon from '@mui/icons-material/UnfoldLessDouble'
import UnfoldMoreDoubleIcon from '@mui/icons-material/UnfoldMoreDouble'
import { Divider, List, ListItem, Drawer as MuiDrawer, Skeleton, Theme } from '@mui/material'
import { CSSObject, styled } from '@mui/material/styles'
import { match } from 'ts-pattern'

import UiSlice from '../slices/ui'

import { useAppDispatch, useAppSelector } from '~/app/hooks'
import { StyledDarkListItemButton, StyledListItemIcon } from '~/core/components/list'
import type { Routes } from '~/core/components/sidebar-nav'
import { SidebarNav } from '~/core/components/sidebar-nav'
import { UserApi } from '~/services/fm/user'

const expandedLazyImageWidth = 100
const collapsedLazyImageWidth = 40
const expandedUsernameSkeleton = 30
const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '&::-webkit-scrollbar': {
    background: 'none',
    width: '8px',
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    background: '#797777',
  },
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- mui 官方案例 https://mui.com/material-ui/react-drawer/#mini-variant-drawer
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- mui 官方案例 https://mui.com/material-ui/react-drawer/#mini-variant-drawer
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    // eslint-disable-next-line @typescript-eslint/naming-convention -- mui 样式覆盖
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    // eslint-disable-next-line @typescript-eslint/naming-convention -- mui 样式覆盖
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

const StyledUserInfo = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-direction: column;
  padding: 10px 10px;
`

const StyledCircleLazyImage = styled(CircleLazyImage)`
  margin: 10px 40px;
  transition: width 0.2s ease, height 0.2s ease;
`

const StyledDivider = styled(Divider)`
  border-color: #585858;
`

const StyledUsernameSpan = styled('span')`
  font-size: 20px;
  color: white;
`

export const Sidebar = (props: { routes: Routes; showUserInfo: boolean }) => {
  const dispatch = useAppDispatch()
  const sidebarState = useAppSelector((state) => state.Ui.sidebarState)
  const sidebarExpanded = sidebarState === 'Expanded'

  const userInfo = UserApi.getUserInfo.useQuery(undefined, { refetchOnMountOrArgChange: true })

  return (
    <StyledDrawer
      variant="permanent"
      open={sidebarExpanded}
      PaperProps={{
        color: 'secondary',
        sx: { bgcolor: 'secondary.main', top: 42, height: 'calc(100% - 42px)' },
      }}
    >
      {match(props.showUserInfo)
        .with(false, () => <></>)
        .otherwise(() => (
          <StyledUserInfo>
            {match(userInfo.isLoading || !userInfo.data || !userInfo?.data?.user)
              .with(true, () => (
                <>
                  <Skeleton
                    sx={{
                      margin: '10px 40px', // 与 StyledCircleLazyImage 保持一致
                      bgcolor: 'grey.700', // 因为底色是深色，所以这里的颜色需要稍微浅一点
                    }}
                    variant="circular" // 圆形骨架
                    width={sidebarExpanded ? expandedLazyImageWidth : collapsedLazyImageWidth} // 侧边栏关闭时同步缩小
                    height={sidebarExpanded ? expandedLazyImageWidth : collapsedLazyImageWidth} // 侧边栏关闭时同步缩小
                  />
                  <Skeleton
                    variant="rounded" // 圆角骨架
                    width={'100%'} // 全宽
                    height={sidebarExpanded ? expandedUsernameSkeleton : 0} // 侧边栏关闭时隐藏文字
                    sx={{
                      display: sidebarExpanded ? 'initial' : 'none', // 侧边栏关闭时隐藏文字
                      bgcolor: 'grey.700', // 因为底色是深色，所以这里的颜色需要稍微浅一点
                    }}
                  />
                </>
              ))
              .otherwise(() => (
                <>
                  <StyledCircleLazyImage
                    src={api.removeUrlProtocol(userInfo.data?.user?.iconurl || '')}
                    alt={userInfo.data?.user?.username || ''}
                    width={sidebarExpanded ? expandedLazyImageWidth : collapsedLazyImageWidth} // 侧边栏关闭时同步缩小
                    height={sidebarExpanded ? expandedLazyImageWidth : collapsedLazyImageWidth} // 侧边栏关闭时同步缩小
                  />
                  <StyledUsernameSpan
                    sx={{
                      height: sidebarExpanded ? 'initial' : '0', // 侧边栏关闭时隐藏文字
                      display: sidebarExpanded ? 'initial' : 'none', // 侧边栏关闭时隐藏文字
                    }}
                  >
                    {userInfo.data?.user?.username || ''}
                  </StyledUsernameSpan>
                </>
              ))}
          </StyledUserInfo>
        ))}
      <StyledDivider />
      <SidebarNav sidebarOpen={sidebarExpanded} routes={props.routes} />
      {/* WORKAROUND: 下面的 List 是一个展开/收缩导航栏的 icon，迁移完了之后要把 display:none; 移除掉 */}
      <List sx={{ display: 'none' }}>
        <ListItem disablePadding>
          <StyledDarkListItemButton onClick={() => dispatch(UiSlice.actions.toggleSidebar())} color="secondary">
            <StyledListItemIcon
              sx={{
                color: 'white', // 因为是深色的背景，所以这里的颜色是白色
                cursor: 'pointer',
              }}
            >
              {match(sidebarExpanded)
                .with(true, () => (
                  <UnfoldLessDoubleIcon
                    sx={{
                      transform: 'rotate(90deg)', // mui 的这个图标是纵向的，此处旋转一下
                    }}
                  />
                ))
                .otherwise(() => (
                  <UnfoldMoreDoubleIcon
                    sx={{
                      transform: 'rotate(-90deg)', // mui 的这个图标是纵向的，此处旋转一下
                    }}
                  />
                ))}
            </StyledListItemIcon>
          </StyledDarkListItemButton>
        </ListItem>
      </List>
    </StyledDrawer>
  )
}
